/**
 * DataSourceReference.ts - References to Interactr data sources
 * 
 * CHANGE HISTORY:
 * - 251201_0410 danielbeer: Created to reference Interactr sources instead of duplicating configs
 * 
 * ARCHITECTURE:
 * - Aggregatr stores references to Interactr sources (IDs only)
 * - Interactr owns the actual data source configurations
 * - This allows all tools to share data sources
 */

/**
 * Reference to an Interactr data source
 */
export interface InteractrSourceReference {
  /** Reference to Interactr data source */
  interactrSourceId: string;
  
  /** Display name (cached from Interactr) */
  name: string;
  
  /** Source type (cached from Interactr) */
  type: string;
  
  /** Whether this source is enabled in this dataset */
  enabled?: boolean;
  
  /** Priority for execution order in this dataset */
  priority?: number;
  
  /** Optional: cached display info for UI */
  cachedInfo?: {
    server?: string;
    database?: string;
    [key: string]: any;
  };
}

/**
 * Reference to a Profylr collection (internal data)
 */
export interface ProfylrSourceReference {
  /** Profylr collection name */
  profylrCollection: string;
  
  /** Display name */
  name: string;
  
  /** Source type */
  type: 'profylr';
  
  /** Whether this source is enabled */
  enabled?: boolean;
  
  /** Priority for execution order */
  priority?: number;
  
  /** Optional filter */
  filter?: Record<string, any>;
}

/**
 * Reference to Fylr files
 */
export interface FylrSourceReference {
  /** Fylr query/path */
  fylrPath: string;
  
  /** Display name */
  name: string;
  
  /** Source type */
  type: 'fylr';
  
  /** Whether this source is enabled */
  enabled?: boolean;
  
  /** Priority for execution order */
  priority?: number;
  
  /** File type filter */
  fileTypes?: string[];
}

/**
 * Union type for all data source references
 */
export type DataSourceReference = 
  | InteractrSourceReference 
  | ProfylrSourceReference 
  | FylrSourceReference;

/**
 * Type guard for Interactr source
 */
export function isInteractrSource(ref: DataSourceReference): ref is InteractrSourceReference {
  return 'interactrSourceId' in ref;
}

/**
 * Type guard for Profylr source
 */
export function isProfylrSource(ref: DataSourceReference): ref is ProfylrSourceReference {
  return 'profylrCollection' in ref;
}

/**
 * Type guard for Fylr source
 */
export function isFylrSource(ref: DataSourceReference): ref is FylrSourceReference {
  return 'fylrPath' in ref;
}
